import { useSelector } from 'store';
import { Contact } from 'types/contact';
import makeAccessLink from 'utils/makeAccessLink';
import useSetPath from './data-saving/useSetPath';
import useSendEmail, { FirebaseEmail } from './useSendEmail';
import useHandleAccess from './data-saving/useHandleAccess';
import useErrorSnackbar from './useErrorSnackbar';

export default function useSendOnboarding() {
  const { emailTemplates } = useSelector((state) => state.config.mainConfig);
  const sendEmail = useSendEmail();
  const setPath = useSetPath();
  const handleAccess = useHandleAccess();
  const errorSnackbar = useErrorSnackbar();

  const sendOnboarding = async (contact: Contact): Promise<boolean> => {
    if (!contact.email1) {
      errorSnackbar({
        message: `No email found for one or more onboarding messages`
      } as Error);
      return false;
    }
    let emailObject, template;
    if (contact.accountTypes?.includes?.('Staff')) {
      template = emailTemplates.find((template) => template.name === 'Onboarding - Talent');
      if (!template) return false;
      emailObject = {
        message: {
          subject: template.subject,
          html: template.html
        }
      };
    } else if (contact.accountTypes?.includes?.('Client')) {
      template = emailTemplates.find((template) => template.name === 'Onboarding - Client');
      if (!template) return false;
      emailObject = {
        message: {
          subject: template.subject,
          html: template.html
        }
      };
    } else if (contact.accountTypes?.includes?.('Talent')) {
      template = emailTemplates.find((template) => template.name === 'Onboarding - Talent');
      if (!template) return false;
      emailObject = {
        message: {
          subject: template.subject,
          html: template.html
        }
      };
    }

    if (!emailObject) return false;

    const accessLink = makeAccessLink(contact.email1 || '', contact.accountTypes).message;

    emailObject.message.html = emailObject.message.html + accessLink;

    const email: FirebaseEmail = {
      to: contact.email1 || '',
      ...emailObject
    };

    try {
      await handleAccess(contact.email1, true);
      const emailQueued = await sendEmail(email);
      if (!emailQueued) return false;
      const stamped = await setPath(`contacts/${contact.id}`, { onboardingSent: new Date() });
      if (!stamped) {
        errorSnackbar({ message: 'Onboarding email was queued, but its sent date could not be saved.' });
      }
      return stamped;
    } catch (error: any) {
      errorSnackbar(error);
      return false;
    }
  };

  return sendOnboarding;
}
